/**
 * Sets up lazy loading for script tags using Intersection Observer.
 *
 * This function looks for script tags with a `data-src` attribute and loads them
 * when they come into view (with a 100px margin). This is useful for scripts
 * that are only needed when the user scrolls to specific sections of the page.
 *
 * Usage:
 * - Add `data-src` instead of `src` to script tags you want to lazy load
 * - The script will be loaded when the element comes into view
 * - All other attributes (like `defer`, `async`, etc.) are preserved
 *
 * Example:
 * <script data-src="https://example.com/script.js" defer></script>
 */
export function setupLazyScriptLoading() {
  // Check if IntersectionObserver is supported
  if (!('IntersectionObserver' in window)) {
    // Fallback for browsers without IntersectionObserver support
    const lazyScripts = document.querySelectorAll('script[data-src]');
    lazyScripts.forEach(script => {
      const src = script.dataset.src;
      if (src) {
        script.src = src;
        script.removeAttribute('data-src');
      }
    });
    return;
  }

  const lazyScriptObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        // If the observed element is a container, find all lazy scripts within it
        if (entry.target.tagName !== 'SCRIPT') {
          const lazyScripts = entry.target.querySelectorAll('script[data-src]');

          lazyScripts.forEach(lazyScript => {
            loadScript(lazyScript);
          });

          // Stop observing the container
          observer.unobserve(entry.target);
        } else {
          // Direct script observation (for head scripts)
          loadScript(entry.target);
          observer.unobserve(entry.target);
        }
      }
    });
  }, {
    rootMargin: '100px',
    threshold: 0.1
  });

  // Helper function to load a script
  function loadScript(lazyScript) {
    const src = lazyScript.dataset.src;
    if (src) {
      // Create a new script element with the src
      const newScript = document.createElement('script');

      // Copy all attributes from the original script
      Array.from(lazyScript.attributes).forEach(attr => {
        if (attr.name !== 'data-src') {
          newScript.setAttribute(attr.name, attr.value);
        }
      });

      // Set the src attribute
      newScript.src = src;

      // Replace the original script with the new one
      lazyScript.parentNode.replaceChild(newScript, lazyScript);
    }
  }

  // Find and observe all lazy scripts
  const lazyScripts = document.querySelectorAll('script[data-src]');
  lazyScripts.forEach(script => {
    // For scripts not in head, observe their parent container instead
    const isInHead = script.closest('head') !== null;
    if (!isInHead) {
      // Find the closest visible parent (footer, section, etc.)
      const parentContainer = script.closest('footer, section, div, body');
      if (parentContainer) {
        lazyScriptObserver.observe(parentContainer);
      } else {
        lazyScriptObserver.observe(script);
      }
    } else {
      lazyScriptObserver.observe(script);
    }
  });
}